import { jwtDecode } from "jwt-decode";

const baseUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api/";

const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

export const SESSION_EXPIRED_EVENT = "auth:session-expired";

export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired. Please log in again.");
    this.name = "SessionExpiredError";
  }
}

let refreshPromise = null;

export const getAccessToken = () => {
  return localStorage.getItem("access_token");
};

export const getRefreshToken = () => {
  return localStorage.getItem("refresh_token");
};

export const clearAuthSession = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("nickname");
};

export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    return (
      typeof exp !== "number" ||
      exp <= currentTime + TOKEN_EXPIRY_BUFFER_SECONDS
    );
  } catch {
    return true;
  }
};

export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    clearAuthSession();
    throw new SessionExpiredError();
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await fetch(baseUrl + "auth/token/refresh/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: refreshToken,
        }),
      });

      if (!response.ok) {
        clearAuthSession();
        throw new SessionExpiredError();
      }

      const data = await response.json();

      if (!data.access) {
        clearAuthSession();
        throw new SessionExpiredError();
      }

      localStorage.setItem("access_token", data.access);

      if (data.refresh) {
        localStorage.setItem("refresh_token", data.refresh);
      }

      return data.access;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

export const hasAuthenticatedSession = async () => {
  const accessToken = getAccessToken();

  if (!isTokenExpired(accessToken)) return true;

  try {
    await refreshAccessToken();
    return true;
  } catch {
    return false;
  }
};

const notifySessionExpired = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
};

const getUsableAccessToken = async () => {
  let accessToken = getAccessToken();

  if (isTokenExpired(accessToken)) {
    accessToken = await refreshAccessToken();
  }

  return accessToken;
};

export const fetchWithToken = async (url, options = {}) => {
  let accessToken;

  try {
    accessToken = await getUsableAccessToken();
  } catch (error) {
    notifySessionExpired();
    throw error;
  }

  let response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (response.status === 401) {
    try {
      const newAccessToken = await refreshAccessToken();

      response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newAccessToken}`,
        },
      });
    } catch (error) {
      notifySessionExpired();
      throw error;
    }

    if (response.status === 401) {
      clearAuthSession();
      notifySessionExpired();
      throw new SessionExpiredError();
    }
  }

  return response;
};
export const fetchNotices = async ({ signal } = {}) => {
  const response = await fetch(baseUrl + "notices/", {
    method: "GET",
    signal,
    cache: "no-store",
  });
  if (!response.ok) throw new Error("Failed to fetch notices");
  const data = await response.json();
  if (!Array.isArray(data)) throw new Error("Invalid notices response");
  return data;
};

// api.js
export const fetchAnswers = async (params = {}, { signal } = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await fetchWithToken(
    baseUrl + "answers/" + (query ? `?${query}` : ""),
    {
      method: "GET",
      signal,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch answers");
  }

  return await response.json();
};

export const getPublicAnswerShareUrl = (answerId) => {
  const encodedAnswerId = encodeURIComponent(String(answerId));
  return new URL(`share/${encodedAnswerId}/`, baseUrl).toString();
};

export const fetchAnswer = async (id) => {
  const response = await fetchWithToken(baseUrl + `answers/${id}/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch the answer");
  }

  return await response.json();
};

export const uploadAnswer = async (
  subject,
  content,
  content_html,
  template_type = "law_school_council",
  draftKey = null,
  metadata = {},
) => {
  const response = await fetchWithToken(baseUrl + "answers/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(draftKey ? { "X-Answer-Draft-Key": draftKey } : {}),
    },
    body: JSON.stringify({
      subject,
      content,
      content_html,
      template_type,
      ...metadata,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to upload answer");
  }

  return response;
};

export const modifyAnswer = async (
  answer_id,
  content,
  content_html,
  draftKey = null,
  metadata = {},
) => {
  const response = await fetchWithToken(baseUrl + `answers/${answer_id}/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(draftKey ? { "X-Answer-Draft-Key": draftKey } : {}),
    },
    body: JSON.stringify({
      content,
      content_html,
      ...metadata,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to modify answer");
  }

  return response;
};

const getAnswerDraftHeaders = (draftToken, draftKey) => ({
  "Content-Type": "application/json",
  ...(draftToken ? { "X-Answer-Draft-Token": draftToken } : {}),
  ...(draftKey ? { "X-Answer-Draft-Key": draftKey } : {}),
});

const requestAnswerDraft = (options, authenticated) =>
  authenticated
    ? fetchWithToken(baseUrl + "answer-draft/", options)
    : fetch(baseUrl + "answer-draft/", options);

export const fetchAnswerDraft = async ({
  authenticated = false,
  draftToken = null,
  draftKey = null,
} = {}) => {
  const response = await requestAnswerDraft(
    {
      method: "GET",
      headers: getAnswerDraftHeaders(draftToken, draftKey),
    },
    authenticated,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch the answer draft");
  }

  return await response.json();
};

export const saveAnswerDraft = async (
  draft,
  {
    authenticated = false,
    draftToken = null,
    draftKey = null,
    keepalive = false,
  } = {},
) => {
  const response = await requestAnswerDraft(
    {
      method: "PUT",
      headers: getAnswerDraftHeaders(draftToken, draftKey),
      body: JSON.stringify(draft),
      keepalive,
    },
    authenticated,
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.detail || "Failed to save the answer draft");
    error.code = data.code;
    error.draft = data.draft;
    error.status = response.status;
    throw error;
  }

  return await response.json();
};

export const deleteAnswerDraft = async ({
  authenticated = false,
  draftToken = null,
  draftKey = null,
  keepalive = false,
} = {}) => {
  const response = await requestAnswerDraft(
    {
      method: "DELETE",
      headers: getAnswerDraftHeaders(draftToken, draftKey),
      keepalive,
    },
    authenticated,
  );

  if (!response.ok) {
    throw new Error("Failed to delete the answer draft");
  }

  return response;
};

export const updateAnswerFavorite = async (answerId, isFavorite) => {
  const response = await fetchWithToken(
    baseUrl + `answers/${answerId}/favorite/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_favorite: isFavorite }),
    },
  );
  if (!response.ok) throw new Error("즐겨찾기를 변경하지 못했습니다.");
  return response.json();
};

export const updateAnswerTitle = async (answerId, newTitle) => {
  const response = await fetchWithToken(baseUrl + `answers/${answerId}/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ subject: newTitle }),
  });

  if (!response.ok) {
    throw new Error("Failed to update the answer title");
  }
  return response;
};

export const togglePublicAccess = async (answer_id, isPublic) => {
  const response = await fetchWithToken(baseUrl + `answers/${answer_id}/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ is_public: !isPublic }),
  });

  if (!response.ok) {
    throw new Error("Failed to toggle public access");
  }

  return await response;
};

export const deleteUser = async () => {
  const response = await fetchWithToken(baseUrl + "auth/delete/", {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error("Failed to delete user");
  }

  return response;
};

export const updateUser = async (agreed_to_terms, agreed_to_privacy) => {
  const response = await fetchWithToken(baseUrl + "auth/update/", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ agreed_to_terms, agreed_to_privacy }),
  });

  if (!response.ok) {
    throw new Error("Failed to update user");
  }
};

const getPasswordChangeErrorMessage = (data) => {
  const errorFields = [
    "current_password",
    "new_password",
    "new_password_confirm",
    "detail",
  ];

  for (const field of errorFields) {
    const message = data?.[field];

    if (Array.isArray(message) && message.length > 0) return message[0];
    if (typeof message === "string") return message;
  }

  return "비밀번호를 변경하지 못했습니다. 잠시 후 다시 시도해주세요.";
};

export const changePassword = async (
  currentPassword,
  newPassword,
  newPasswordConfirm,
) => {
  const response = await fetchWithToken(baseUrl + "auth/password/", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    }),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(getPasswordChangeErrorMessage(data));
  }

  if (!data.access || !data.refresh) {
    throw new Error("새 인증 정보를 받지 못했습니다. 다시 로그인해주세요.");
  }

  localStorage.setItem("access_token", data.access);
  localStorage.setItem("refresh_token", data.refresh);

  return data;
};

// api.js
export const deleteAnswer = async (id) => {
  const response = await fetchWithToken(baseUrl + `answers/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete the answer");
  }
  return response;
};

export const LogIn = async (email, password) => {
  const response = await fetch(baseUrl + "auth/login/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      password: password,
    }),
  });

  if (!response.ok) {
    throw new Error("Login failed");
  }

  const data = await response.json();
  return data;
};

export const fetchUserInfo = async () => {
  const response = await fetchWithToken(baseUrl + "auth/user-info/", {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user info");
  }

  return await response.json();
};

export const signUp = async (email, nickname, password) => {
  const response = await fetch(baseUrl + "auth/register/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      nickname,
      password,
    }),
  });

  if (!response.ok) {
    throw new Error("Registration failed");
  }

  return await response.json();
};

export const fetchPublicAnswer = async (id) => {
  const response = await fetch(baseUrl + `view/${id}/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch the answer");
  }
  const data = await response.json();
  return data;
};
